import { Form } from "@inertiajs/react"
import {
  Coins,
  Gift,
  Package,
  PackageCheck,
  Sparkles,
  Truck,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import type { RedeemProfileAddress } from "@/components/coins/redeem-ship-to"
import { TrackingNumberLine } from "@/components/collab-card/tracking-number-line"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { formatRelativeTime } from "@/lib/relative-time"
import { cn } from "@/lib/utils"
import { creatorRedemptionReceiptPath } from "@/routes"

import {
  RedemptionAddressDrawer,
  type RedemptionDestination,
} from "./redemption-address-drawer"

/**
 * OKS-407 — A single acquired-reward row on the creator "My rewards"
 * history (`/rewards/mine`, board `594:415`). Cover-first, mirroring
 * the W24 catalog card: 80px cover (or kind-icon fallback), title +
 * status badge (with dot), coins paid (snapshot, amber), and relative
 * dates ("Redeemed {when}" + terminal "Fulfilled/Cancelled {when}").
 *
 * ⚠️ SHARED by TWO surfaces — `/rewards/mine` (the full history) AND the
 * inline "Your Redemptions" block on `/rewards` (OKS-556 §19 wallet).
 * Both controllers serialise the same wire shape deliberately so this
 * component is written once. Editing it lights both; forking it would
 * drift them. Both serialisers must therefore carry every field added
 * here (OKS-563 added `shipped_at` + `tracking_number` to both).
 *
 * OKS-563 — fourth status variant, `shipped`, which is NON-terminal:
 * the row is still cancellable and the brand can still act on it.
 */

/**
 * Mirrors `Redemption::STATUSES` (Ruby). ⚠️ Nothing cross-checks the two —
 * no guard, no codegen — and Vitest cannot see Ruby drift, so this is a
 * parallel convention maintained by review, not a derivation.
 */
export type RedemptionStatus = "pending" | "shipped" | "fulfilled" | "cancelled"

/**
 * Mirrors `Redemption::TERMINAL_STATUSES`. `shipped` is deliberately
 * absent — see the Ruby constant for why a positive enumeration rather
 * than `!== "pending"`.
 */
export const TERMINAL_STATUSES: readonly RedemptionStatus[] = [
  "fulfilled",
  "cancelled",
] as const

export interface RedemptionHistoryItem {
  id: number
  status: RedemptionStatus
  coin_cost_snapshot: number
  created_at: string
  shipped_at: string | null
  tracking_number: string | null
  /**
   * OKS-985 — the carrier link when the operator pasted one (PRD AC9).
   * Preferred over `tracking_number` for the Track affordance; with neither,
   * the row says so and links NOWHERE.
   */
  tracking_url: string | null
  fulfilled_at: string | null
  cancelled_at: string | null
  /**
   * OKS-985/994 — where this one is going: the FULL stored destination, all six
   * `Shipments::ADDRESS_FIELDS`. `null` on a legacy gear redemption with no
   * snapshot, which is what raises the "Add delivery address" prompt.
   *
   * ⚠️ It carried city + country only while the creator had no edit
   * affordance. OKS-994 gave them one and widened the producer
   * (`CreatorRedemptionSerialisation#ships_to_for`) to all six fields, because
   * the correction drawer must open on THIS destination — prefilling from the
   * profile address would overwrite what the creator actually typed. This
   * comment was not updated with the producer and said city+country until
   * OKS-997 touched the file.
   *
   * ⚠️ Deliberately NOT named `shipping_address`. That token is the
   * BRAND-facing gated key whose contract is "absent outside the window";
   * this is the creator's own row, ungated. One name, one contract.
   */
  ships_to: RedemptionDestination | null
  /**
   * OKS-997 — the creator's own receipt assertion (PRD D3). A FLAG, not a
   * status: a received row is still `shipped` until the brand fulfils it, and
   * `status === "fulfilled"` with a non-null `received_at` is a legitimate,
   * expected combination (UX Spec §3.1).
   */
  received_at: string | null
  reward: {
    id: number | null
    title: string | null
    kind: string | null
    /** OKS-985 — drives the gear-only affordances. */
    category: string | null
    cover_image_url: string | null
  }
}

/**
 * OKS-985 — ⚠️ DEFENCE IN DEPTH on an XSS boundary, not the primary guard.
 *
 * This value is rendered as an `href`, and a `javascript:` scheme would
 * execute in the reading creator's own session. The real guard is
 * server-side, in `Redemptions::MarkShipped#safe_tracking_url` — anything
 * non-https is dropped before it is stored, because `update_all` bypasses the
 * model validator and a validator alone would be hollow.
 *
 * This exists because the row also renders rows written BEFORE that guard
 * landed, and because a second reader of this column should not have to
 * rediscover the hazard. `https:` only — not "not javascript:", which is a
 * denylist and therefore a game of catch-up with `data:`, `vbscript:` and
 * whatever comes next.
 */
function isSafeTrackingUrl(url: string | null): url is string {
  return typeof url === "string" && /^https:\/\//i.test(url)
}

const KIND_ICON: Record<string, typeof Gift> = {
  product: Package,
  experience: Sparkles,
  custom: Gift,
}

// Colour is never the sole signal — the chip always carries its text
// label too (UX Spec §7).
const STATUS_DOT: Record<RedemptionStatus, string> = {
  pending: "bg-amber-500",
  shipped: "bg-blue-500",
  fulfilled: "bg-emerald-500",
  cancelled: "bg-muted-foreground",
}

export function RedemptionHistoryRow({
  profileAddress,
  redemption,
}: {
  redemption: RedemptionHistoryItem
  /** OKS-985 — prefill for the legacy-address drawer; null when none. */
  profileAddress?: RedeemProfileAddress | null
}) {
  const { t, i18n } = useTranslation()
  const { reward, status } = redemption
  const title = reward.title ?? t("rewards.mine.deleted_reward")

  const redeemedWhen = formatRelativeTime(
    redemption.created_at,
    t,
    i18n.language,
    "rewards.mine.time",
  )

  // OKS-563 — the second date slot. A POSITIVE equality chain falling to
  // null, so a future state degrades to "no second date" rather than
  // rendering a wrong one. `shipped` joins it despite being non-terminal:
  // the row reads "Redeemed X · Shipped Y" in the same slot.
  //
  // Deliberately NOT keyed off `TERMINAL_STATUSES` — a cancelled-after-
  // shipped row shows "Cancelled", not "Shipped" (UX Spec §4 Edge), so
  // the LAST transition wins, which is what this chain expresses.
  const terminalAt =
    status === "fulfilled"
      ? redemption.fulfilled_at
      : status === "cancelled"
        ? redemption.cancelled_at
        : status === "shipped"
          ? redemption.shipped_at
          : null

  const terminalWhen = terminalAt
    ? formatRelativeTime(terminalAt, t, i18n.language, "rewards.mine.time")
    : null

  // Tracking renders ONLY on a shipped row. A cancelled-after-shipped row
  // OKS-985 — the gear-only affordances. Both clauses matter: a perks
  // redemption has no destination to show, and a shipped one is past the
  // point where supplying an address would reach anybody.
  const isPendingGear =
    status === "pending" && redemption.reward.category === "gear"

  // keeps `tracking_number` in the DB (PRD R6 — audit, not display) but
  // must not show it: the parcel is no longer coming.
  const showTracking = status === "shipped"

  // OKS-997 — the receipt slot (UX Spec §3.1), MUTUALLY EXCLUSIVE on
  // `received_at` and evaluated in this order:
  //
  //   received_at !== null                      -> the confirmed LINE, any status
  //   status === "shipped" && received_at null  -> the BUTTON
  //   otherwise                                 -> nothing
  //
  // ⚠️ `received_at` is tested FIRST, not `status`. A row fulfilled AFTER
  // receipt must keep saying so — the creator's assertion is not erased by the
  // brand's later one — and ordering the status test first would silently drop
  // the line on exactly those rows.
  const receiptConfirmed = redemption.received_at !== null
  const showReceiptButton = status === "shipped" && !receiptConfirmed
  const receivedWhen = redemption.received_at
    ? formatRelativeTime(
        redemption.received_at,
        t,
        i18n.language,
        "rewards.mine.time",
      )
    : null

  return (
    <div
      data-testid="redemption-history-row"
      data-redemption-id={redemption.id}
      data-status={status}
      className="bg-card flex items-stretch gap-3 rounded-xl border p-3"
    >
      <Cover reward={reward} title={title} />

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className="line-clamp-2 text-sm font-semibold"
            title={title}
            data-testid="redemption-history-title"
          >
            {title}
          </h3>
          <span
            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
            data-testid="redemption-history-status"
          >
            <span
              aria-hidden="true"
              className={cn("size-1.5 rounded-full", STATUS_DOT[status])}
            />
            {t(`rewards.mine.status.${status}`)}
          </span>
        </div>

        <span
          className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 tabular-nums"
          data-testid="redemption-history-cost"
        >
          <Coins className="size-4 text-amber-700" aria-hidden="true" />
          {t("rewards.mine.coins_paid", {
            count: redemption.coin_cost_snapshot,
          })}
        </span>

        <p className="text-muted-foreground text-xs">
          {t("rewards.mine.redeemed_when", { when: redeemedWhen })}
          {terminalWhen && (
            <>
              {" · "}
              {t(`rewards.mine.terminal_when.${status}`, {
                when: terminalWhen,
              })}
            </>
          )}
        </p>

        {/* OKS-985 — the destination sub-line and the legacy prompt
            (UX Spec §2.4). Gear + pending only: every other category ships
            nothing, and every other status is past the point where the
            address matters. */}
        {isPendingGear &&
          (redemption.ships_to ? (
            /* OKS-994 — the destination sub-line NOW CARRIES THE CORRECTION
               ENTRY POINT. Before this ticket the drawer lived only in the
               else-branch below, i.e. only where there was no address, so a
               creator who could see a typo had nowhere to fix it. */
            <div className="flex flex-col items-start gap-2">
              <p
                className="text-muted-foreground text-xs"
                data-testid="redemption-history-ships-to"
              >
                {t("rewards.mine.ships_to", {
                  city: redemption.ships_to.city,
                  country: redemption.ships_to.country,
                })}
              </p>
              <RedemptionAddressDrawer
                redemptionId={redemption.id}
                profileAddress={profileAddress ?? null}
                // ⚠️ The stored destination, NOT the profile address. Passing
                // null here would prefill the correction form from the profile
                // and overwrite this reward's real destination on save.
                destination={redemption.ships_to}
              />
            </div>
          ) : (
            /* ⚠️ DEFAULT variant, not destructive. This is a nudge, not a
               failure — the creator has done nothing wrong, they claimed
               before we started asking (UX Spec §2.4). */
            <Alert data-testid="redemption-history-address-alert">
              <AlertDescription className="flex flex-col items-start gap-2">
                {t("rewards.mine.address_needed")}
                <RedemptionAddressDrawer
                  redemptionId={redemption.id}
                  profileAddress={profileAddress ?? null}
                  // ADD mode: there is no stored destination by construction —
                  // this branch is the `ships_to === null` side.
                  destination={null}
                />
              </AlertDescription>
            </Alert>
          ))}

        {/* OKS-985 — a real Track anchor when a link was given (PRD AC9).
            ⚠️ Checked BEFORE `tracking_number` because a URL-only shipment is
            the common case under a link-first dialog, and because
            `TrackingNumberFromUrl` refuses to guess on an unknown carrier —
            so the link is often the ONLY thing this row has. */}
        {showTracking && isSafeTrackingUrl(redemption.tracking_url) ? (
          <a
            href={redemption.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary flex items-center gap-1.5 text-xs underline-offset-4 hover:underline"
            data-testid="redemption-history-track"
          >
            <Truck className="size-3.5 shrink-0" aria-hidden="true" />
            {t("rewards.mine.tracking.track")}
          </a>
        ) : null}

        {showTracking &&
          !isSafeTrackingUrl(redemption.tracking_url) &&
          (redemption.tracking_number ? (
            <TrackingNumberLine
              value={redemption.tracking_number}
              testId="redemption-history-tracking"
              labelKey="rewards.mine.tracking.present"
              copiedMessageKey="rewards.mine.tracking.copied"
              ariaLabelKey="rewards.mine.tracking.aria"
            />
          ) : (
            // PRD R5 — no tracking is a legitimate, gracefully-handled
            // state, not an error. Plain text, NOT a button: there is
            // nothing to copy, and a disabled-looking control would read
            // as something the creator failed to activate.
            <p
              className="text-muted-foreground flex items-center gap-1.5 text-xs"
              data-testid="redemption-history-tracking-pending"
            >
              <Truck className="size-3.5 shrink-0" aria-hidden="true" />
              {t("rewards.mine.tracking.absent")}
            </p>
          ))}

        {/* OKS-997 — the receipt slot, LAST in the text column and on its own
            line (UX Spec §3.1 position 6 / §7). Never inline beside tracking:
            at 402px the row's text column is `min-w-0 flex-1` and a control
            sharing that line forces horizontal scroll. */}
        {showReceiptButton && (
          <Form
            action={creatorRedemptionReceiptPath(redemption.id)}
            method="post"
            options={{ preserveScroll: true }}
          >
            {({ processing }: { processing: boolean }) => (
              <Button
                type="submit"
                variant="outline"
                size="sm"
                disabled={processing}
                data-testid="redemption-history-receipt"
                aria-label={t("rewards.mine.receipt.aria", { title })}
                className="h-11 rounded-full text-sm font-semibold"
              >
                <Package aria-hidden="true" className="size-4" />
                {processing
                  ? t("rewards.mine.receipt.saving")
                  : t("rewards.mine.receipt.confirm")}
              </Button>
            )}
          </Form>
        )}

        {/* ⚠️ TEXT, not a disabled button — same reason as `tracking.absent`
            above: a disabled control reads as something the creator failed to
            activate, when in fact they succeeded. */}
        {receiptConfirmed && (
          <p
            className="text-muted-foreground flex items-center gap-1.5 text-xs"
            data-testid="redemption-history-received"
          >
            <PackageCheck className="size-3.5 shrink-0" aria-hidden="true" />
            {t("rewards.mine.receipt.received_when", { when: receivedWhen })}
          </p>
        )}
      </div>
    </div>
  )
}

function Cover({
  reward,
  title,
}: {
  reward: RedemptionHistoryItem["reward"]
  title: string
}) {
  const [errored, setErrored] = useState(false)
  const KindIcon = KIND_ICON[reward.kind ?? "custom"] ?? Gift
  const showImage = Boolean(reward.cover_image_url) && !errored

  if (showImage) {
    return (
      <img
        src={reward.cover_image_url!}
        alt={title}
        loading="lazy"
        onError={() => setErrored(true)}
        data-testid="redemption-history-cover"
        className="size-20 shrink-0 rounded-lg object-cover"
      />
    )
  }

  return (
    <div
      aria-hidden="true"
      data-testid="redemption-history-cover-fallback"
      className="bg-muted text-muted-foreground flex size-20 shrink-0 items-center justify-center rounded-lg"
    >
      <KindIcon className="size-7" />
    </div>
  )
}
